// oxlint-disable react/jsx-pascal-case
import { type Component, type ComponentProps, type JSX, Show } from "solid-js";
import { Dynamic } from "solid-js/web";
import { cn } from "@gymlog/shared";

interface EmptyStateProps {
  icon: Component<ComponentProps<"svg">>;
  title: string;
  description?: string;
  class?: string;
  /**
   * Optional SVG illustration (theme-aware). If provided, replaces the icon
   * circle with a more expressive illustration with gym identity.
   */
  illustration?: Component<ComponentProps<"svg">>;
  /** Optional action (e.g. navigation/retry buttons) below description. */
  action?: JSX.Element;
  /**
   * When provided, the icon/illustration becomes an interactive target
   * that triggers the same action as the primary CTA (opening a dialog, navigating,
   * or focusing an input), so the empty-state is never a dead end.
   * Tapping the illustration is a discoverable shortcut to the CTA — leave
   * undefined for purely informational empty-states (remains decorative).
   */
  onIconActivate?: () => void;
  /**
   * Accessible label for interactive icon (recommended when defining
   * `onIconActivate`). Should describe the action, e.g. "Create your first routine".
   */
  iconActionLabel?: string;
}

/**
 * Premium empty-state: brand-aware illustration with gentle float, clear hierarchy,
 * and an optional CTA. When `onIconActivate` is wired, the icon/illustration
 * becomes a real `<button>` (native tap/click/keyboard Enter/Space) with
 * visible focus ring; otherwise, it remains decorative (`aria-hidden`). All
 * animation is suppressed under `prefers-reduced-motion` via global guard.
 */
export const EmptyState: Component<EmptyStateProps> = (props) => {
  const interactive = () => props.onIconActivate;

  return (
    <div
      class={cn("flex flex-1 flex-col items-center justify-center p-8 text-center", props.class)}
    >
      <Show
        when={props.illustration}
        fallback={
          <div class="animate-scale-in">
            {/* When there is an action, the icon becomes a button (Enter/Space for free
                being a <button>); otherwise, it remains decorative. Float is suppressed
                by the global prefers-reduced-motion guard. */}
            <Show
              when={interactive()}
              fallback={
                <div class="mb-6 rounded-full bg-primary/5 p-6 ring-1 ring-primary/10 animate-float">
                  <props.icon class="h-12 w-12 text-primary/60" />
                </div>
              }
            >
              {(activate) => (
                <button
                  type="button"
                  aria-label={props.iconActionLabel ?? props.title}
                  onClick={() => {
                    activate()();
                  }}
                  class="group mb-6 rounded-full bg-primary/5 p-6 ring-1 ring-primary/10 animate-float transition-[transform,box-shadow,background-color] duration-200 hover:bg-primary/10 hover:ring-primary/30 hover:shadow-lg hover:shadow-primary/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background active:scale-95 motion-safe:hover:scale-105"
                >
                  <props.icon class="h-12 w-12 text-primary/60 transition-colors group-hover:text-primary" />
                </button>
              )}
            </Show>
          </div>
        }
      >
        {(Illustration) => (
          <Show
            when={interactive()}
            fallback={
              <div class="mb-6 animate-scale-in">
                <Dynamic component={Illustration()} class="h-36 w-36 animate-float" />
              </div>
            }
          >
            {(activate) => (
              <button
                type="button"
                aria-label={props.iconActionLabel ?? props.title}
                onClick={() => {
                  activate()();
                }}
                class="mb-6 animate-scale-in rounded-3xl p-2 transition-[transform,box-shadow] duration-200 hover:shadow-lg hover:shadow-primary/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background active:scale-95 motion-safe:hover:scale-105"
              >
                <Dynamic component={Illustration()} class="h-36 w-36 animate-float" />
              </button>
            )}
          </Show>
        )}
      </Show>
      <h3 class="text-lg font-semibold tracking-tight">{props.title}</h3>
      <Show when={props.description}>
        <p class="mt-2 text-sm text-muted-foreground max-w-xs mx-auto">{props.description}</p>
      </Show>
      <Show when={props.action}>
        <div class="mt-6">{props.action}</div>
      </Show>
    </div>
  );
};
